import React, { Fragment, useEffect, useState } from "react";
import { Backdrop, CircularProgress, Container } from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  cloneDeep,
  find,
  includes,
  isNil,
  map,
  some,
  toNumber,
} from "lodash";
import { formatCategoryName, isLegacyProductFilterValue } from "../../utils/helpers";
import { ApiCallStatus } from "../../utils/constants";
import { getLegacyProductRedirectUrl } from "../../api/productController";
import {
  fetchCategoriesAndTypes,
  fetchProductsData,
  fetchVendorsData,
} from "../PageFunctions/productFunctions";
import ProductGrid from "../../components/ProductGrid";
import { fetchProductStockInfo } from "../PageFunctions/priceFunctions";
import { useArgusAuth } from "../../hooks/useArgusAuth";
import MakeModelYear from "../../components/MakeModelYear";
import Breadcrumbs from "../../components/Breadcrumbs/Breadcrumbs";


const ProductsPage = ({ genericDataLoaded, genericPageContent }) => {
  const { isAuthenticated, isLoading } = useArgusAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { productCategory, productType } = useParams();
  const [products, setProducts] = useState(null);
  const [vendors, setVendors] = useState(null);
  const [activeVendor, setActiveVendor] = useState(null);
  const [pricingData, setPricingData] = useState(null);
  const [quantities, setQuantities] = useState(null);
  const [categoryFilters, setCategoryFilters] = useState([]);
  const [typeFilters, setTypeFilters] = useState([]);
  const [vendorFilters, setVendorFilters] = useState([]);
  const [makeFilters, setMakeFilters] = useState([]);
  const [modelFilters, setModelFilters] = useState([]);
  const [variantFilters, setVariantFilters] = useState([]);
  const [yearFilters, setYearFilters] = useState([]);
  const [activeFilters, setActiveFilters] = useState({
    productCategory: null,
    productType: null,
    vendor: null,
    make: null,
    model: null,
    year: null,
    variant: null,
    pageNumber: null,
  });

  const [categoriesAndTypes, setCategoriesAndTypes] = React.useState([]);
  const [total, setTotal] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(18);

  const [productsCallStatus, setProductsCallStatus] = useState(
    ApiCallStatus.NotStarted,
  );
  const [filtersCallStatus, setFiltersCallStatus] = useState(
    ApiCallStatus.NotStarted,
  );
  const [stockInfoCallStatus, setStockInfoCallStatus] = useState(
    ApiCallStatus.NotStarted,
  );
  const [
    fetchCategoriesAndTypesCallStatus,
    setFetchCategoriesAndTypesCallStatus,
  ] = useState(ApiCallStatus.NotStarted);
  const [fetchVendorsDataCallStatus, setFetchVendorsDataCallStatus] = useState(
    ApiCallStatus.NotStarted,
  );


  const fetchProductsDataAsync = async () => {
    const productQueryParams = [
      activeFilters.productCategory,
      activeFilters.productType,
      activeFilters.vendor,
    ];
    const vehicleQueryParams = [
      activeFilters.make,
      activeFilters.model,
      activeFilters.variant,
      activeFilters.year,
    ];

    const atLeastOneProductQueryParam = some(productQueryParams, (x) => !!x);
    const atLeastOneVehicleQueryParam = some(vehicleQueryParams, (x) => !!x);

    if (!!atLeastOneProductQueryParam || !!atLeastOneVehicleQueryParam) {
      await fetchProductsData({
        productCategoryId: activeFilters.productCategory,
        productTypeId: activeFilters.productType,
        vendorId: activeFilters.vendor,
        makeId: activeFilters.make,
        modelId: activeFilters.model,
        variantId: activeFilters.variant,
        yearId: activeFilters.year,
        pageNumber: activeFilters.pageNumber,
        setProducts,
        setCategoryFilters,
        setTypeFilters,
        setVendorFilters,
        setMakeFilters,
        setModelFilters,
        setVariantFilters,
        setYearFilters,
        setTotal,
        setPageSize,
        setStatusInProgress: () =>
          setProductsCallStatus(ApiCallStatus.InProgress),
        setStatusSuccess: () => setProductsCallStatus(ApiCallStatus.Succeeded),
        setStatusFailed: () => setProductsCallStatus(ApiCallStatus.Failed),
        setStatusError: () => setProductsCallStatus(ApiCallStatus.Error),
      });
    }
  };

  const fetchProductStockInfoAsync = async () => {
    const productSkus = map(products, (x) => x.sku);

    //If no products, set empty array instead of returning undefined
    if (!productSkus || productSkus.length === 0) {
      setPricingData([]);
      setQuantities([]);
      setStockInfoCallStatus(ApiCallStatus.Succeeded);
      return;
    }

    await fetchProductStockInfo({
      productSkus,
      setPricingData,
      setQuantities,
      setStatusInProgress: () =>
        setStockInfoCallStatus(ApiCallStatus.InProgress),
      setStatusSuccess: () => setStockInfoCallStatus(ApiCallStatus.Succeeded),
      setStatusFailed: () => setStockInfoCallStatus(ApiCallStatus.Failed),
      setStatusError: () => setStockInfoCallStatus(ApiCallStatus.Error),
    });
  };

  const fetchCategoriesAndTypesAsync = async () => {
    await fetchCategoriesAndTypes({
      setCategoriesAndTypesData: setCategoriesAndTypes,
      setStatusInProgress: () =>
        setFetchCategoriesAndTypesCallStatus(ApiCallStatus.InProgress),
      setStatusSuccess: () =>
        setFetchCategoriesAndTypesCallStatus(ApiCallStatus.Succeeded),
      setStatusFailed: () =>
        setFetchCategoriesAndTypesCallStatus(ApiCallStatus.Failed),
      setStatusError: () =>
        setFetchCategoriesAndTypesCallStatus(ApiCallStatus.Error),
    });
  };

  const fetchVendorsDataAsync = async () => {
    await fetchVendorsData({
      setVendorData: setVendors,
      setStatusInProgress: () =>
        setFetchVendorsDataCallStatus(ApiCallStatus.InProgress),
      setStatusSuccess: () =>
        setFetchVendorsDataCallStatus(ApiCallStatus.Succeeded),
      setStatusFailed: () =>
        setFetchVendorsDataCallStatus(ApiCallStatus.Failed),
      setStatusError: () => setFetchVendorsDataCallStatus(ApiCallStatus.Error),
    });
  };

  const handlePageNumberChange = async (event, value) => {
    await updateUrlQueryValue("pageNumber", Number(value));
  };

  const updateUrlQueryValue = async (key, value) => {
    setProductsCallStatus(ApiCallStatus.NotStarted);
    const searchParams = new URLSearchParams(location.search);

    searchParams.set(key, value);
    window.location.href = `${location.pathname}${
      !!searchParams.size > 0 ? "?" : ""
    }${searchParams.toString()}`;
  };

  const updateQueryParameters = async (queryParameters) => {
    window.location.href = `${location.pathname}${
      !!queryParameters ? "?" : ""
    }${queryParameters}`;
  };

  const scrollTop = () => {
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    scrollTop();

    const searchParams = new URLSearchParams(location.search);
    const vendorParam = searchParams.get("vendor");
    const makeParam = searchParams.get("make");
    const modelParam = searchParams.get("model");
    const variantParam = searchParams.get("variant");
    const yearParam = searchParams.get("year");

    const isLegacyProductUrl =
      isLegacyProductFilterValue(productCategory, { allowAll: true }) ||
      isLegacyProductFilterValue(productType) ||
      isLegacyProductFilterValue(vendorParam) ||
      isLegacyProductFilterValue(makeParam) ||
      isLegacyProductFilterValue(modelParam) ||
      isLegacyProductFilterValue(variantParam) ||
      isLegacyProductFilterValue(yearParam);

    if (isLegacyProductUrl) {
      window.location.href = getLegacyProductRedirectUrl({
        productCategory,
        productType,
        vendor: vendorParam,
        make: makeParam,
        model: modelParam,
        variant: variantParam,
        year: yearParam,
        pageNumber: searchParams.get("pageNumber"),
      });
      return;
    }

    if (isNil(productCategory)) {
      navigate({
        pathname: "/products/all",
        search: `?${searchParams.toString()}`,
      });
    }

    const activeFiltersCopy = cloneDeep(activeFilters);

    const pn = searchParams.get("pageNumber");
    const page = pn ? Number(pn) : 1;

    activeFiltersCopy.make = toNumber(searchParams.get("make"));
    activeFiltersCopy.model = toNumber(searchParams.get("model"));
    activeFiltersCopy.year = toNumber(searchParams.get("year"));
    activeFiltersCopy.variant = toNumber(searchParams.get("variant"));
    activeFiltersCopy.vendor = toNumber(searchParams.get("vendor"));
    activeFiltersCopy.pageNumber = page;
    activeFiltersCopy.productCategory = productCategory;
    activeFiltersCopy.productType = toNumber(productType);

    setPageNumber(page);
    setProductsCallStatus(ApiCallStatus.NotStarted);
    setActiveFilters(activeFiltersCopy);
  }, [location]);

  useEffect(() => {
    if (genericDataLoaded === true && isLoading === false) {
      if (isAuthenticated === false) {
        fetchCategoriesAndTypesAsync();
        fetchProductsDataAsync();
        fetchVendorsDataAsync();
      } else {
        fetchCategoriesAndTypesAsync();
        fetchProductsDataAsync();
        fetchVendorsDataAsync();
      }
    }
  }, [genericDataLoaded, isAuthenticated, isLoading]);

  useEffect(() => {
    setFiltersCallStatus(ApiCallStatus.NotStarted);

    if (!!activeFilters) {
      if (productsCallStatus === ApiCallStatus.NotStarted) {
        fetchProductsDataAsync();
      }

      if (
        fetchVendorsDataCallStatus === ApiCallStatus.Succeeded &&
        !!activeFilters.vendor &&
        !!vendors &&
        vendors.length > 0
      ) {
        const vendorToUse = find(
          vendors,
          (x) => x.vendorId === activeFilters.vendor,
        );
        setActiveVendor(vendorToUse);
      }
    }
  }, [activeFilters, productsCallStatus, fetchVendorsDataCallStatus, vendors]);

  useEffect(() => {
    if (
      productsCallStatus === ApiCallStatus.Succeeded &&
      !!products &&
      products.length > 0
    ) {
      fetchProductStockInfoAsync();
    }
  }, [productsCallStatus, products]);

  const allLoadingStates = [
    productsCallStatus,
    fetchCategoriesAndTypesCallStatus,
    fetchVendorsDataCallStatus,
    stockInfoCallStatus,
  ];

  const pageIsLoading =
    includes(allLoadingStates, ApiCallStatus.InProgress) ||
    genericDataLoaded === false ||
    isLoading === true;

  return (
    <>
      <Container maxWidth="lg" sx={{ mb: 4 }}>
        <Breadcrumbs
          segmentLabels={(() => {
            const matched = categoriesAndTypes.find(
              (c) => String(c.productCategoryId) === productCategory,
            );
            return matched
              ? {
                  [productCategory]: formatCategoryName(
                    matched.productCategoryName,
                  ),
                }
              : {};
          })()}
        />
      </Container>
      <MakeModelYear showHeader={false} />
      <Container maxWidth="lg">
        <ProductGrid
          products={products}
          pricingData={pricingData}
          stockInfo={quantities}
          activeVendor={activeVendor}
          customerCategory={genericPageContent?.customerCategory}
          categoriesAndTypes={categoriesAndTypes}
          categoryFilters={categoryFilters}
          typeFilters={typeFilters}
          vendorFilters={vendorFilters}
          makeFilters={makeFilters}
          modelFilters={modelFilters}
          variantFilters={variantFilters}
          yearFilters={yearFilters}
          activeFilters={activeFilters}
          setActiveFilters={setActiveFilters}
          total={total}
          pageNumber={pageNumber}
          pageSize={pageSize}
          handlePageNumberChange={handlePageNumberChange}
          updateQueryParameters={updateQueryParameters}
          loading={pageIsLoading}
          filtersLoading={filtersCallStatus === ApiCallStatus.InProgress}
        />
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={pageIsLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Container>
    </>
  );
};
export default ProductsPage;
